
import { ArtItem, Order, Comment, OrderStatus, User, ArtCategory, ChatThread, ChatMessage, DailyVisit, CommissionTier, SiteSettings, ProfileScreenshot } from '../types';
import { INITIAL_ARTS, DEFAULT_CATEGORY_COVERS } from '../constants';
import { storage } from './storageService';
import { detectVisitorGeoLocation } from '../geoUtils';

const SITE_URL = window.location.origin;

const getApiUrl = (file: string) => {
  const clean = file.replace(/^\//, '');
  const path = window.location.pathname;
  const dir = path.substring(0, path.lastIndexOf('/') + 1);
  return `${window.location.origin}${dir}${clean}`;
};

export const database = {
  resolveMediaUrl(path: string) {
    if (!path || path.trim() === '') return '';
    if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) return path;
    const cleanPath = path.replace(/^\//, '');
    
    // Se o caminho já começa com 'uploads/', não duplicamos
    if (cleanPath.startsWith('uploads/')) {
      return getApiUrl(cleanPath);
    }
    
    return getApiUrl(`uploads/${cleanPath}`);
  },

  processArtItem(art: ArtItem): ArtItem {
    const resolvedImage = this.resolveMediaUrl(art.imageUrl);
    const resolvedVideo = art.videoUrl ? this.resolveMediaUrl(art.videoUrl) : '';
    
    return {
      ...art,
      imageUrl: resolvedImage,
      videoUrl: resolvedVideo,
      isAnimated: !!art.videoUrl || !!art.isAnimated
    };
  },

  parseRawAnalytics(rawData: any): DailyVisit[] {
    if (!rawData) return [];
    
    let target = rawData;
    if (target.analytics) target = target.analytics;
    else if (target.data && typeof target.data === 'object' && !Array.isArray(target.data)) target = target.data;

    let list: DailyVisit[] = [];

    if (Array.isArray(target)) {
      list = target.map((item: any) => ({
        date: item.date || new Date().toISOString().split('T')[0],
        count: item.uniqueCount || item.count || item.total || item.totalCount || 0,
        uniqueCount: item.uniqueCount || item.unique || item.count || 0,
        totalCount: item.totalCount || item.total || item.count || 0,
        countries: item.countries || {}
      }));
    } else if (typeof target === 'object') {
      list = Object.entries(target).map(([date, data]: [string, any]) => ({
        date,
        count: data?.unique || data?.total || 0,
        uniqueCount: data?.unique || 0,
        totalCount: data?.total || 0,
        countries: data?.countries || {}
      }));
    }

    const validList = list
      .filter(item => typeof item.date === 'string' && item.date.length >= 8)
      .sort((a, b) => a.date.localeCompare(b.date));

    if (validList.length > 0) {
      storage.save('analytics_cache', validList, 'registry').catch(() => {});
      try {
        localStorage.setItem('niiko_analytics_cache', JSON.stringify(validList));
      } catch (e) {}
    }

    return validList;
  },

  async logVisit(): Promise<{ success: boolean; data?: any; analytics?: DailyVisit[] }> {
    try {
      let visitorId = localStorage.getItem('niiko_visitor_id');
      if (!visitorId) {
        visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        localStorage.setItem('niiko_visitor_id', visitorId);
      }

      // Detect visitor geolocation (Country, Region, State)
      const geo = await detectVisitorGeoLocation();
      const params = new URLSearchParams({
        vid: visitorId,
        cc: geo.countryCode || 'BR',
        country: geo.countryName || 'Brasil',
        region: geo.regionName || 'Principal',
        city: geo.cityName || '',
        _t: Date.now().toString()
      });

      const endpoint = getApiUrl(`track_visit.php?${params.toString()}`);
      const res = await fetch(endpoint, {
        method: 'GET',
        cache: 'no-store'
      });

      if (res.ok) {
        const data = await res.json();
        let parsedList: DailyVisit[] | undefined = undefined;
        if (data.analytics) {
          parsedList = this.parseRawAnalytics(data.analytics);
        }
        return { success: true, data, analytics: parsedList };
      }
      return { success: false };
    } catch (e) {
      console.warn("Analytics log failed or offline:", e);
      return { success: false };
    }
  },

  async getAnalytics(): Promise<DailyVisit[]> {
    // 1. Tenta buscar diretamente pelo endpoint PHP com cache busting
    try {
      const phpRes = await fetch(getApiUrl(`track_visit.php?action=get_analytics&v=${Date.now()}`), {
        cache: 'no-store'
      });
      if (phpRes.ok) {
        const rawData = await phpRes.json();
        const list = this.parseRawAnalytics(rawData);
        if (list && list.length > 0) return list;
      }
    } catch (e) {}

    // 2. Fallback: tenta buscar diretamente pelo arquivo visits.json
    try {
      const res = await fetch(getApiUrl(`visits.json?v=${Date.now()}`), {
        cache: 'no-store'
      });
      if (res.ok) {
        const rawData = await res.json();
        const list = this.parseRawAnalytics(rawData);
        if (list && list.length > 0) return list;
      }
    } catch (e) {}

    // 3. Fallback do cache local IndexedDB
    const cached = await storage.load('analytics_cache', 'registry');
    if (Array.isArray(cached) && cached.length > 0) {
      return cached;
    }

    // 4. Fallback do localStorage
    try {
      const lsCached = localStorage.getItem('niiko_analytics_cache');
      if (lsCached) {
        const parsed = JSON.parse(lsCached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}

    return [];
  },

  async getArts(): Promise<{ data: ArtItem[], source: string }> {
    try {
      const remoteArts = await this.getRemoteArts();
      if (remoteArts && remoteArts.length > 0) {
        // Filter out locally deleted arts
        const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
        const filteredArts = remoteArts.filter(a => !deleted.includes(a.id));
        
        const processedArts = filteredArts.map(art => this.processArtItem(art));
        await storage.save('arts', processedArts);
        return { data: processedArts, source: 'hostinger_server' };
      }
      let cached = await storage.load('arts');
      if (cached && cached.length > 0) {
        const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
        const filteredCached = cached.filter((a: any) => !deleted.includes(a.id));
        const processedCached = filteredCached.map((art: ArtItem) => this.processArtItem(art));
        return { data: processedCached, source: 'local_cache' };
      }
      return { data: INITIAL_ARTS.map(art => this.processArtItem(art)), source: 'initial_demos' };
    } catch (e) {
      return { data: INITIAL_ARTS.map(art => this.processArtItem(art)), source: 'error_fallback' };
    }
  },

  async getRemoteArts(): Promise<ArtItem[] | null> {
    try {
      const res = await fetch(`${SITE_URL}/arts.json?v=${Date.now()}`); 
      if (!res.ok) return null;
      const data = await res.json();
      return Array.isArray(data) ? data : null;
    } catch (e) {
      return null;
    }
  },

  async deleteArt(id: string): Promise<boolean> {
    try {
      // Track deleted ID locally
      const deleted: string[] = await storage.load('deleted_arts', 'registry') || [];
      if (!deleted.includes(id)) {
        await storage.save('deleted_arts', [...deleted, id], 'registry');
      }

      const res = await fetch(`${SITE_URL}/delete_art.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (res.ok) {
        const cached: ArtItem[] = await storage.load('arts') || [];
        await storage.save('arts', cached.filter(a => a.id !== id));
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },

  async updateArtMetadata(art: ArtItem): Promise<boolean> {
    try {
      const res = await fetch(`${SITE_URL}/update_art.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(art)
      });
      if (res.ok) {
        const cached: ArtItem[] = await storage.load('arts') || [];
        const updated = cached.map(a => a.id === art.id ? art : a);
        await storage.save('arts', updated);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },

  async uploadNewArt(formData: FormData): Promise<{ success: boolean, item?: ArtItem, error?: string }> {
    try {
      const res = await fetch(`${SITE_URL}/upload_art.php`, {
        method: 'POST',
        body: formData
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        if (text.includes('<!DOCTYPE html>') || text.includes('<html')) {
           return { success: false, error: "Arquivo 'upload_art.php' não encontrado no servidor ou sem permissão." };
        }
        return { success: false, error: `O servidor PHP não está respondendo corretamente.` };
      }
      
      if (result.status === 'success' && result.item) {
        const processed = this.processArtItem(result.item);
        const cached: ArtItem[] = await storage.load('arts') || [];
        await storage.save('arts', [processed, ...cached]);
        return { success: true, item: processed };
      }
      return { success: false, error: result.message || 'Erro desconhecido no servidor' };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    }
  },

  async getCategoryCovers(): Promise<Record<string, string>> {
    try {
      const res = await fetch(`${SITE_URL}/category_covers.json?v=${Date.now()}`);
      if (res.ok) {
        const remoteCovers = await res.json();
        const resolved: Record<string, string> = { ...DEFAULT_CATEGORY_COVERS };
        Object.entries(remoteCovers).forEach(([cat, path]) => {
          if (path) {
            resolved[cat] = this.resolveMediaUrl(path as string);
          }
        });
        await storage.save('category_covers', remoteCovers, 'registry');
        return resolved;
      }
    } catch (e) {}
    
    try {
      const cached = await storage.load('category_covers', 'registry');
      if (cached) {
        const resolved: Record<string, string> = { ...DEFAULT_CATEGORY_COVERS };
        Object.entries(cached).forEach(([cat, path]) => {
          if (path) {
            resolved[cat] = this.resolveMediaUrl(path as string);
          }
        });
        return resolved;
      }
    } catch (e) {}
    
    return DEFAULT_CATEGORY_COVERS;
  },

  async saveCategoryCovers(covers: Record<string, string>): Promise<boolean> {
    try {
      const cleanCovers: Record<string, string> = {};
      Object.entries(covers).forEach(([cat, url]) => {
        if (url.includes('/uploads/')) {
          cleanCovers[cat] = url.split('/').pop() || '';
        } else {
          cleanCovers[cat] = url;
        }
      });

      const res = await fetch(`${SITE_URL}/save_category_covers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanCovers)
      });
      
      if (res.ok) {
        await storage.save('category_covers', cleanCovers, 'registry');
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  },

  async syncAllCoversToServer(currentCovers: Record<string, string>): Promise<boolean> {
    return this.saveCategoryCovers(currentCovers);
  },

  async getCommissionTiers(): Promise<CommissionTier[]> {
    try {
      const res = await fetch(`${SITE_URL}/commission_tiers.json?v=${Date.now()}`);
      if (res.ok) {
        const remoteTiers = await res.json();
        if (Array.isArray(remoteTiers)) {
          const resolved = remoteTiers.map(t => ({
            ...t,
            demoUrl: this.resolveMediaUrl(t.demoUrl)
          }));
          await storage.save('commission_tiers', remoteTiers, 'registry');
          return resolved;
        }
      }
    } catch (e) {}
    // Fallback to constants
    const { DEFAULT_COMMISSION_TIERS } = await import('../constants');
    return DEFAULT_COMMISSION_TIERS.map(t => ({
      ...t,
      demoUrl: this.resolveMediaUrl(t.demoUrl)
    }));
  },

  async saveCommissionTiers(tiers: CommissionTier[]): Promise<{ success: boolean, error?: string }> {
    try {
      const cleanTiers = tiers.map(t => {
        let cleanUrl = t.demoUrl;
        if (cleanUrl.includes('/uploads/')) {
          cleanUrl = cleanUrl.split('/').pop() || '';
        }
        return { ...t, demoUrl: cleanUrl };
      });

      const res = await fetch(`${SITE_URL}/save_commission_tiers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanTiers)
      });
      
      if (res.ok) {
        const text = await res.text();
        try {
          const result = JSON.parse(text.trim());
          if (result.status === 'success') {
            await storage.save('commission_tiers', cleanTiers, 'registry');
            return { success: true };
          } else {
            return { success: false, error: result.message || 'Erro reportado pelo servidor.' };
          }
        } catch (e) {
          return { success: false, error: `Erro ao processar resposta do servidor.` };
        }
      } else {
        return { success: false, error: `Servidor retornou status ${res.status}` };
      }
    } catch (e) {
      return { success: false, error: (e as Error).message };
    }
  },

  async uploadCommissionTierCover(tierId: string, file: File): Promise<{ success: boolean, url?: string, error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tierId', tierId);
      
      const res = await fetch(`${SITE_URL}/upload_tier_cover.php`, {
        method: 'POST',
        body: formData
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        return { success: false, error: `Erro na resposta do servidor.` };
      }
      
      if (result.status === 'success') {
        return { success: true, url: this.resolveMediaUrl(result.filename) };
      }
      return { success: false, error: result.message || 'Erro de upload' };
    } catch (e) {
      return { success: false, error: "Falha na conexão." };
    }
  },

  async uploadCategoryCover(category: string, file: File): Promise<{ success: boolean, url?: string, error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      
      const res = await fetch(`${SITE_URL}/upload_category_cover.php`, {
        method: 'POST',
        body: formData
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        if (text.includes('<!DOCTYPE html>') || text.includes('<html')) {
          return { success: false, error: "Arquivo 'upload_category_cover.php' não encontrado no servidor ou sem permissão." };
        }
        return { success: false, error: `Resposta inválida do servidor.` };
      }
      
      if (result.status === 'success') {
        return { success: true, url: this.resolveMediaUrl(result.filename) };
      }
      return { success: false, error: result.message || 'Erro de upload desconhecido' };
    } catch (e) {
      return { success: false, error: "Falha na conexão com o servidor Hostinger." };
    }
  },

  async uploadCoverImage(file: File): Promise<{ success: boolean, url?: string, error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'google_cover');
      
      const res = await fetch(`${SITE_URL}/upload_category_cover.php`, {
        method: 'POST',
        body: formData
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        return { success: false, error: "Resposta inválida do servidor ao subir capa." };
      }
      
      if (result.status === 'success') {
        return { success: true, url: this.resolveMediaUrl(result.filename) };
      }
      return { success: false, error: result.message || 'Erro de upload da foto de capa' };
    } catch (e) {
      return { success: false, error: "Falha na conexão ao subir foto de capa." };
    }
  },

  async syncUserToServer(user: User): Promise<boolean> {
    try {
      const res = await fetch(`${SITE_URL}/save_user.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user)
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  },

  async signIn(email: string, pass: string) {
    const normalizedEmail = email.trim().toLowerCase();

    // 1. First attempt: Authenticate directly against the server API
    try {
      const res = await fetch(`${SITE_URL}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, password: pass })
      });
      const data = await res.json();
      
      if (res.ok && data.status === 'success' && data.user) {
        const user: User = data.user;
        const users: User[] = await storage.load('users', 'registry') || [];
        const filtered = users.filter(u => u.email.toLowerCase() !== normalizedEmail);
        await storage.save('users', [...filtered, user], 'registry');
        localStorage.setItem('niiko_session', JSON.stringify(user));
        return { data: { user }, error: null };
      }
      
      if (res.status === 401 || data.message === 'Senha incorreta.') {
        return { data: null, error: { message: 'Senha incorreta.' } };
      }
    } catch (e) {
      console.warn('Falha na requisição direta ao login do servidor:', e);
    }

    // 2. Second attempt: Check remote users.json directly (cross-device database)
    try {
      const res = await fetch(`${SITE_URL}/users.json?v=${Date.now()}`, { cache: 'no-store' });
      if (res.ok) {
        const remoteUsers = await res.json();
        if (Array.isArray(remoteUsers)) {
          const found = remoteUsers.find((u: any) => u.email && u.email.toLowerCase() === normalizedEmail);
          if (found) {
            if (found.password && pass && String(found.password) !== String(pass)) {
              return { data: null, error: { message: 'Senha incorreta.' } };
            }
            const users: User[] = await storage.load('users', 'registry') || [];
            const filtered = users.filter(u => u.email.toLowerCase() !== normalizedEmail);
            await storage.save('users', [...filtered, found], 'registry');
            localStorage.setItem('niiko_session', JSON.stringify(found));
            return { data: { user: found }, error: null };
          }
        }
      }
    } catch (e) {}

    // 3. Third attempt: Check local storage (IndexedDB)
    const localUsers: User[] = await storage.load('users', 'registry') || [];
    const localUser = localUsers.find(u => u.email && u.email.toLowerCase() === normalizedEmail);
    if (localUser) {
      if (localUser.password && pass && String(localUser.password) !== String(pass)) {
        return { data: null, error: { message: 'Senha incorreta.' } };
      }
      // If found locally, immediately sync to server so PC and other devices can log in too!
      this.syncUserToServer(localUser).catch(() => {});
      localStorage.setItem('niiko_session', JSON.stringify(localUser));
      return { data: { user: localUser }, error: null };
    }

    return { data: null, error: { message: 'Identidade não encontrada.' } };
  },

  async signUp(email: string, pass: string, name: string) {
    const normalizedEmail = email.trim().toLowerCase();
    const cleanName = (name && name.trim()) || normalizedEmail.split('@')[0];

    const newUser: User = {
      name: cleanName,
      email: normalizedEmail,
      avatarUrl: '',
      password: pass
    };

    // 1. Register on server first so other devices (PC, mobile) immediately recognize it
    try {
      const res = await fetch(`${SITE_URL}/register.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cleanName, email: normalizedEmail, password: pass })
      });
      const data = await res.json();

      if (!res.ok || data.status === 'error') {
        return { data: null, error: { message: data.message || 'E-mail já existe.' } };
      }

      const registeredUser: User = data.user || newUser;
      const users: User[] = await storage.load('users', 'registry') || [];
      const filtered = users.filter(u => u.email.toLowerCase() !== normalizedEmail);
      await storage.save('users', [...filtered, registeredUser], 'registry');
      localStorage.setItem('niiko_session', JSON.stringify(registeredUser));
      return { data: { user: registeredUser }, error: null };
    } catch (e) {
      console.warn('Erro na requisição ao register.php do servidor:', e);

      // Try save_user.php as fallback
      try {
        await fetch(`${SITE_URL}/save_user.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newUser)
        });
      } catch (err) {}

      const users: User[] = await storage.load('users', 'registry') || [];
      const exists = users.find(u => u.email.toLowerCase() === normalizedEmail);
      if (exists) return { data: null, error: { message: 'E-mail já existe.' } };

      await storage.save('users', [...users, newUser], 'registry');
      localStorage.setItem('niiko_session', JSON.stringify(newUser));
      return { data: { user: newUser }, error: null };
    }
  },

  async getCurrentUser(): Promise<User | null> {
    const session = localStorage.getItem('niiko_session');
    if (!session) return null;
    try {
      const user: User = JSON.parse(session);
      // Auto-sync active session to server in background if not already synced
      if (user && user.email) {
        this.syncUserToServer(user).catch(() => {});
      }
      return user;
    } catch (e) {
      return null;
    }
  },

  async getAllUsers(): Promise<User[]> {
    let remoteUsers: User[] = [];
    try {
      const res = await fetch(`${SITE_URL}/users.json?v=${Date.now()}`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          remoteUsers = data;
        }
      }
    } catch (e) {}

    const localUsers: User[] = await storage.load('users', 'registry') || [];
    
    // Also aggregate any user emails from orders and chats that might not be registered
    const ordersRes = await this.getOrders();
    const orders = ordersRes.data || [];
    const chats = await this.getChatThreads();

    const userMap = new Map<string, User>();

    // Add remote users first
    remoteUsers.forEach(u => {
      if (u.email) userMap.set(u.email.toLowerCase(), u);
    });

    // Add local users (preserves password/avatar if local has it)
    let hasNewLocalToSync = false;
    localUsers.forEach(u => {
      if (u.email) {
        const key = u.email.toLowerCase();
        if (!userMap.has(key)) {
          userMap.set(key, u);
          hasNewLocalToSync = true;
        } else {
          // Merge details (e.g. if local has password or avatar)
          const existing = userMap.get(key)!;
          userMap.set(key, { ...existing, ...u });
        }
      }
    });

    orders.forEach((o: any) => {
      if (o.userEmail && !userMap.has(o.userEmail.toLowerCase())) {
        userMap.set(o.userEmail.toLowerCase(), {
          name: o.userName || o.userEmail.split('@')[0],
          email: o.userEmail.toLowerCase(),
          avatarUrl: ''
        });
      }
    });

    chats.forEach((c: any) => {
      if (c.userEmail && !userMap.has(c.userEmail.toLowerCase())) {
        userMap.set(c.userEmail.toLowerCase(), {
          name: c.userName || c.userEmail.split('@')[0],
          email: c.userEmail.toLowerCase(),
          avatarUrl: ''
        });
      }
    });

    const merged = Array.from(userMap.values());
    await storage.save('users', merged, 'registry');

    // If there were local users not yet in remote users.json, automatically sync to server!
    if (hasNewLocalToSync || merged.length > remoteUsers.length) {
      try {
        await fetch(`${SITE_URL}/save_users.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(merged)
        });
      } catch (err) {}
    }

    return merged;
  },

  async deleteUser(email: string): Promise<boolean> {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const users: User[] = await storage.load('users', 'registry') || [];
      const updated = users.filter(u => u.email.toLowerCase() !== normalizedEmail);
      await storage.save('users', updated, 'registry');

      // Also sync deletion to server
      try {
        await fetch(`${SITE_URL}/delete_user.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: normalizedEmail })
        });
      } catch (e) {}

      return true;
    } catch (e) {
      return false;
    }
  },

  async saveAllUsers(users: User[]): Promise<boolean> {
    try {
      await storage.save('users', users, 'registry');
      try {
        await fetch(`${SITE_URL}/save_users.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(users)
        });
      } catch (e) {}
      return true;
    } catch (e) {
      return false;
    }
  },

  async signOut() { localStorage.removeItem('niiko_session'); },

  async updateUser(updatedData: Partial<User>) {
    const user = await this.getCurrentUser();
    if (!user) return { data: null, error: 'No session' };
    const updated = { ...user, ...updatedData, email: user.email.toLowerCase() };
    const users: User[] = await storage.load('users', 'registry') || [];
    const updatedUsers = users.map(u => u.email.toLowerCase() === user.email.toLowerCase() ? updated : u);
    await storage.save('users', updatedUsers, 'registry');
    localStorage.setItem('niiko_session', JSON.stringify(updated));

    // Sync to server
    try {
      await fetch(`${SITE_URL}/update_user.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
    } catch (e) {
      try {
        await fetch(`${SITE_URL}/save_user.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updated)
        });
      } catch (err) {}
    }

    return { data: updated, error: null };
  },

  async getOrders() { 
    try {
      const res = await fetch(`${SITE_URL}/orders.json?v=${Date.now()}`);
      if (res.ok) {
        let remoteOrders = await res.json();
        if (Array.isArray(remoteOrders)) {
          // Filter out locally deleted orders that might still be in server cache/static file
          const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
          remoteOrders = remoteOrders.filter((o: any) => !deleted.includes(o.id));
          
          await storage.save('orders', remoteOrders);
          return { data: remoteOrders, source: 'hostinger' };
        }
      }
    } catch (e) {}
    const data = await storage.load('orders');
    const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
    const filteredData = Array.isArray(data) ? data.filter((o: any) => !deleted.includes(o.id)) : [];
    return { data: filteredData, source: 'local' }; 
  },

  async saveOrder(order: Order) {
    try {
      await fetch(`${SITE_URL}/save_order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(order)
      });
      const current = await storage.load('orders') || [];
      await storage.save('orders', [order, ...current]);
      return true;
    } catch (e) { return true; }
  },

  async updateOrderStatus(id: string, status: OrderStatus) {
    try {
      await fetch(`${SITE_URL}/update_status.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status })
      });
    } catch (e) {}
    const current = await storage.load('orders') || [];
    const updated = current.map((o: any) => o.id === id ? { ...o, status } : o);
    await storage.save('orders', updated);
    return true;
  },

  async deleteOrder(id: string): Promise<boolean> {
    try {
      // Track deleted ID locally to prevent reappearance from stale server cache
      const deleted: string[] = await storage.load('deleted_orders', 'registry') || [];
      if (!deleted.includes(id)) {
        await storage.save('deleted_orders', [...deleted, id], 'registry');
      }

      await fetch(`${SITE_URL}/delete_order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
    } catch (e) {}
    const current = await storage.load('orders') || [];
    const updated = current.filter((o: any) => o.id !== id);
    await storage.save('orders', updated);
    return true;
  },

  async getChatThreads(): Promise<ChatThread[]> {
    try {
      const res = await fetch(`${SITE_URL}/chats.json?v=${Date.now()}`);
      if (res.ok) {
        let data = await res.json();
        if (Array.isArray(data)) {
          await storage.save('chats', data);
          return data;
        }
      }
    } catch (e) {}
    const cached = await storage.load('chats');
    return Array.isArray(cached) ? cached : [];
  },

  async saveChatMessage(userEmail: string, userName: string, message: ChatMessage) {
    try {
      await fetch(`${SITE_URL}/save_chat.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, userName, message })
      });
      const current = await storage.load('chats') || [];
      const updated = [...current];
      const idx = updated.findIndex((t: any) => t.userEmail === userEmail);
      if (idx > -1) {
        updated[idx].messages.push(message);
        updated[idx].lastUpdated = new Date().toISOString();
        if (userName && userName !== 'Usuário' && userName !== 'Usuario' && userName !== 'User' && userName !== 'Cliente') {
          updated[idx].userName = userName;
        } else if (!updated[idx].userName || updated[idx].userName === 'Usuário' || updated[idx].userName === 'Usuario' || updated[idx].userName === 'User' || updated[idx].userName === 'Cliente') {
          if (userName) updated[idx].userName = userName;
        }
      } else {
        updated.push({
          userEmail,
          userName,
          messages: [message],
          lastUpdated: new Date().toISOString(),
          unreadCount: message.sender === 'user' ? 1 : 0
        });
      }
      await storage.save('chats', updated);
      return true;
    } catch (e) { return false; }
  },

  async deleteChatThread(userEmail: string): Promise<boolean> {
    try {
      await fetch(`${SITE_URL}/delete_chat.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail })
      });
    } catch (e) {}
    const current = await storage.load('chats') || [];
    const updated = current.filter((t: any) => t.userEmail !== userEmail);
    await storage.save('chats', updated);
    return true;
  },

  async getComments() {
    try {
      const res = await fetch(`${SITE_URL}/comments.json?v=${Date.now()}`);
      if (res.ok) {
        const remoteData = await res.json();
        if (Array.isArray(remoteData)) {
          await storage.save('comments', remoteData);
          return { data: remoteData, error: null };
        }
      }
    } catch (e) {}
    const local = await storage.load('comments') || [];
    return { data: local, error: null };
  },
  async saveComment(comment: Comment) {
    try {
      await fetch(`${SITE_URL}/save_comment.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(comment)
      });
    } catch (e) {}
    const current = await storage.load('comments') || [];
    await storage.save('comments', [comment, ...current]);
    return true;
  },
  async saveAllComments(comments: Comment[]) {
    try {
      await fetch(`${SITE_URL}/save_comments.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(comments)
      });
    } catch (e) {}
    await storage.save('comments', comments);
    return true;
  },

  async getSettings(): Promise<SiteSettings> {
    // 1. Tenta carregar do servidor remoto com cache-busting
    try {
      const res = await fetch(getApiUrl(`settings.json?v=${Date.now()}`), { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data && typeof data === 'object') {
          if (!data.faqs) data.faqs = [];
          await storage.save('settings', data, 'registry');
          try {
            localStorage.setItem('niiko_site_settings_backup', JSON.stringify(data));
          } catch (e) {}
          return data;
        }
      }
    } catch (e) {
      console.warn("Erro ao carregar settings.json remoto:", e);
    }

    // 2. Fallback: IndexedDB
    try {
      const cached = await storage.load('settings', 'registry');
      if (cached && typeof cached === 'object') {
        if (!cached.faqs) cached.faqs = [];
        return cached;
      }
    } catch (e) {}

    // 3. Fallback: localStorage
    try {
      const ls = localStorage.getItem('niiko_site_settings_backup');
      if (ls) {
        const parsed = JSON.parse(ls);
        if (parsed && typeof parsed === 'object') {
          if (!parsed.faqs) parsed.faqs = [];
          return parsed;
        }
      }
    } catch (e) {}
    return {
      customRequestIntro_pt: 'Deseja algo que transcenda a galeria comum? Entre em contato com nossos mestres artesãos para solicitar uma arte personalizada para o seu perfil.',
      customRequestIntro_en: 'Looking for something beyond the common gallery? Contact our master craftsmen to request a personalized art for your profile.',
      customRequestIntro_es: '¿Buscas algo más allá de la galería común? Contacta con nuestros maestros artesanos para solicitar un arte personalizado para tu perfil.',
      customRequestIntro_ru: 'Ищете нечто большее, чем обычная галерея? Свяжитесь с нашими мастерами для заказа персонального арта для вашего профиля.',
      deviantartUrl: '',
      telegramUrl: '',
      discordUrl: '',
      faqs: []
    };
  },

  async saveSettings(settings: any): Promise<{ success: boolean, message?: string }> {
    // Salva imediatamente no cache local e no localStorage de backup
    try {
      await storage.save('settings', settings, 'registry');
      localStorage.setItem('niiko_site_settings_backup', JSON.stringify(settings));
    } catch (e) {}

    try {
      const res = await fetch(getApiUrl('save_settings.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (parseError) {
        return { 
          success: true, 
          message: 'Salvo localmente com sucesso no navegador.' 
        };
      }

      if (res.ok && result.status === 'success') {
        return { success: true };
      }
      return { success: true, message: result.message || 'Salvo com sucesso.' };
    } catch (e) {
      console.warn("Erro de conexão ao salvar configurações:", e);
      return { success: true, message: 'Salvo localmente com sucesso.' };
    }
  },

  async getProfileScreenshots(): Promise<ProfileScreenshot[]> {
    // 1. Tenta carregar do servidor remoto com cache-busting
    try {
      const res = await fetch(getApiUrl(`profile_screenshots.json?v=${Date.now()}`), { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          const processed = data.map(item => ({
            ...item,
            imageUrl: this.resolveMediaUrl(item.imageUrl)
          }));
          await storage.save('profile_screenshots', processed, 'registry');
          try {
            localStorage.setItem('niiko_profile_screenshots_backup', JSON.stringify(processed));
          } catch (e) {}
          return processed;
        }
      }
    } catch (e) {
      console.warn("Erro ao buscar profile_screenshots.json:", e);
    }

    // 2. Fallback: IndexedDB
    try {
      const cached = await storage.load('profile_screenshots', 'registry');
      if (Array.isArray(cached) && cached.length > 0) {
        // Tenta sincronizar de volta para o servidor se estiver vazio no servidor
        this.saveProfileScreenshots(cached).catch(() => {});
        return cached;
      }
    } catch (e) {}

    // 3. Fallback: localStorage
    try {
      const ls = localStorage.getItem('niiko_profile_screenshots_backup');
      if (ls) {
        const parsed = JSON.parse(ls);
        if (Array.isArray(parsed) && parsed.length > 0) {
          await storage.save('profile_screenshots', parsed, 'registry');
          this.saveProfileScreenshots(parsed).catch(() => {});
          return parsed;
        }
      }
    } catch (e) {}

    return [];
  },

  async uploadProfileScreenshot(file: File, metadata: { title?: string; userName?: string; steamUrl?: string }): Promise<{ success: boolean; item?: ProfileScreenshot; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.userName) formData.append('user_name', metadata.userName);
      if (metadata.steamUrl) formData.append('steam_url', metadata.steamUrl);

      const targetUrl = getApiUrl('upload_profile_screenshot.php');
      
      try {
        const res = await fetch(targetUrl, {
          method: 'POST',
          body: formData
        });

        const text = await res.text();
        let data: any = null;
        try {
          data = JSON.parse(text);
        } catch (jsonErr) {
          console.warn("upload_profile_screenshot.php raw response:", text);
        }

        if (data && data.status === 'success' && data.item) {
          const item: ProfileScreenshot = {
            ...data.item,
            imageUrl: this.resolveMediaUrl(data.item.imageUrl)
          };
          const current = await this.getProfileScreenshots();
          const updated = [item, ...current.filter(s => s.id !== item.id)];
          await storage.save('profile_screenshots', updated, 'registry');
          try {
            localStorage.setItem('niiko_profile_screenshots_backup', JSON.stringify(updated));
          } catch (e) {}
          return { success: true, item };
        } else if (data && data.message) {
          console.warn("Mensagem do servidor PHP:", data.message);
        }
      } catch (networkErr) {
        console.warn("Erro de rede ao chamar upload_profile_screenshot.php:", networkErr);
      }

      // Se o upload PHP multipart falhou, converte para base64 com persistência completa
      const base64Data = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(URL.createObjectURL(file));
        reader.readAsDataURL(file);
      });

      const fallbackItem: ProfileScreenshot = {
        id: 'SHOT-' + Date.now() + '-' + Math.floor(Math.random() * 900 + 100),
        imageUrl: base64Data,
        title: metadata.title || '',
        userName: metadata.userName || '',
        steamUrl: metadata.steamUrl || '',
        date: new Date().toISOString()
      };

      const current = await this.getProfileScreenshots();
      const updated = [fallbackItem, ...current.filter(s => s.id !== fallbackItem.id)];
      await storage.save('profile_screenshots', updated, 'registry');
      try {
        localStorage.setItem('niiko_profile_screenshots_backup', JSON.stringify(updated));
      } catch (e) {}
      
      // Sincroniza a lista atualizada com o servidor PHP
      await this.saveProfileScreenshots(updated);

      return { success: true, item: fallbackItem };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    }
  },

  async deleteProfileScreenshot(id: string): Promise<boolean> {
    try {
      const current = await this.getProfileScreenshots();
      const updated = current.filter(s => s.id !== id);
      await storage.save('profile_screenshots', updated, 'registry');
      try {
        localStorage.setItem('niiko_profile_screenshots_backup', JSON.stringify(updated));
      } catch (e) {}

      try {
        await fetch(getApiUrl('delete_profile_screenshot.php'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id })
        });
      } catch (e) {}

      await this.saveProfileScreenshots(updated);
      return true;
    } catch (e) {
      return false;
    }
  },

  async saveProfileScreenshots(screenshots: ProfileScreenshot[]): Promise<boolean> {
    try {
      await storage.save('profile_screenshots', screenshots, 'registry');
      try {
        localStorage.setItem('niiko_profile_screenshots_backup', JSON.stringify(screenshots));
      } catch (e) {}

      const cleanList = screenshots.map(s => ({
        ...s,
        imageUrl: s.imageUrl.includes('/uploads/') ? 'uploads/' + s.imageUrl.split('/uploads/')[1] : s.imageUrl
      }));

      const res = await fetch(getApiUrl('save_profile_screenshots.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanList)
      });
      return res.ok;
    } catch (e) {
      return true;
    }
  }
};

